import { CompanyContext } from 'contexts/CompanyContextProvider';
import linkIcon from 'images/icons/svg/link-1.svg';
import warningIcon from 'images/icons/svg/warning.svg';
import { Button } from 'primereact/button';
import React, { useContext, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { EpdLinks } from 'routes/EpdRoutes';
import { ActionState } from 'services/EpdClient';
import styled from 'styled-components';
import { Container, EditFull, InfoBox } from 'styles/Styles.styled';
import { PoorMansError } from 'util/utils';

import { ActionPrimaryButton } from 'components/v2/buttons';
import { YES_NO } from 'components/v2/compiler/constants';
import TransferEPDDialog from 'components/v2/compiler/dialogs/TransferEPDDialog';
import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import WizardTextInputComponent from 'components/v2/epd-wizard-inputs/WizardTextInput';
import TitleAndBack from 'components/v2/TitleAndBack';
import { withGreyBackground } from 'pages/v2/WithGreyBackground';
import { useUpdateEpdProperty } from 'services/api/mutations';
import { useGetCopyGroupId, useGetEPD } from 'services/api/queries';
import { FieldPanel, WizardHeaderButtonBox, WizardHeaderGrid } from 'styles/v2/Styles.styled';
import AuthService from 'services/AuthService';

const EditEpd: React.FunctionComponent = () => {
  const { epdid } = useParams<{ epdid: string }>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const epd = useGetEPD(epdid).data;
  const groupId = useGetCopyGroupId(epd?.versionId!).data;
  const epdUpdateMutation = useUpdateEpdProperty(epdid);
  const { changeCompany } = useContext(CompanyContext);
  const history = useHistory();
  const isLicenseeAdmin = AuthService.isLicenseeAdmin();
  const currentEPDVersionName = [epd?.fullIdentificationNumber, epd?.title].filter(Boolean).join(' ');
  const hasDualRegistration = [
    { value: false, label: YES_NO.NO },
    { value: true, label: YES_NO.YES },
  ];

  const viewEPDObject = () => {
    changeCompany?.(epd?.epdOwner?.companyId);
    history.push(EpdLinks.epd(epdid));
  };

  const onSaveChanges = async (propertyName: string, value: any) => {
    try {
      epdUpdateMutation.mutate({
        epdId: epdid,
        versionId: epd?.versionId as string,
        propertyName: propertyName,
        newValue: value,
      });
    } catch (error) {
      setErrorMessage(PoorMansError(error));
    }
  };

  if (groupId) {
    history.push(EpdLinks.showGroupEpd(epdid));
  }

  return (
    <Container>
      <EditFull>
        <WizardHeaderGrid>
          <TitleAndBack
            title={currentEPDVersionName}
            backTo={'Back to EPDs'}
            onBack={() => history.push(EpdLinks.epdsList())}
          />
          <ToolButtonsContainer>
            <TransferEPDDialog epd={epd} />
            <ActionPrimaryButton
              actionState={ActionState.Enabled}
              style={{ marginLeft: '16px' }}
              label="Export ILCD+EPD"
              onClick={() => history.push(EpdLinks.ilcdEpdMappingList(epdid))}
            />
          </ToolButtonsContainer>
        </WizardHeaderGrid>
        <FieldPanel>
          <div>
            <WizardTextInputComponent
              label="EPD name"
              title={currentEPDVersionName}
              value={currentEPDVersionName}
              name="name"
              disabled
            />
            <ButtonBox>
              <Button
                icon={<ShowDetailsIcon src={linkIcon} alt="link" />}
                style={{ fontSize: 14, fontWeight: 500 }}
                type="button"
                label="View EPD"
                text
                onClick={viewEPDObject}
                disabled={false}
              />
            </ButtonBox>
          </div>

          <WizardTextInputComponent
            label="Registration number"
            value={epd?.registrationNumber}
            name="registrationNumber"
            disabled
            style={{ marginBottom: '26px' }}
          />
          <WizardTextInputComponent
            label="Organisation"
            title={epd?.epdOwner?.companyName}
            value={epd?.epdOwner?.companyName}
            name="organisation"
            disabled
          />
          <CompilerSelectControlledComponent
            label="Dual registration"
            name="IsDualRegistration"
            options={hasDualRegistration}
            value={hasDualRegistration.find((x) => x.value == epd?.isDualRegistration)}
            onChanged={onSaveChanges}
            placeholder="Select..."
            disabled={isLicenseeAdmin}
            isClearable={false}
          />
        </FieldPanel>
        {errorMessage && (
          <InfoBox style={{ borderLeft: 'solid 3px red', display: 'flex', alignItems: 'center' }}>
            <img src={warningIcon} alt="error" style={{ height: '35px', marginRight: '1rem' }} />
            <div style={{ color: 'red' }}>{PoorMansError(errorMessage)}</div>
          </InfoBox>
        )}
      </EditFull>
    </Container>
  );
};

const ShowDetailsIcon = styled.img`
  width: 15px;
  height: 15px;
  margin-right: 5px;
  cursor: pointer;
`;

const ButtonBox = styled.div`
  width: 100%;
  display: flex;
  justify-content: start;
  align-items: center;
  max-height: 70px;
  margin-top: 6px;
  cursor: pointer;

  .p-button.p-button-text {
    padding: 0;
    background-color: transparent;
    border: none;
    ${(props) => props.theme.fonts.standard};
  }
  .p-button.p-button-text:hover {
    background-color: transparent;
  }
  .p-button.p-button-text:disabled {
    color: #757575;
  }
`;

const ToolButtonsContainer = styled(WizardHeaderButtonBox)`
  min-height: 48px;
`;

export default withGreyBackground(EditEpd);
